import React, { useEffect, useMemo, useRef, useState } from "react";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import "./PhoneNumberField.css";
import useCountryCodes from "../../hooks/useCountryCodes";

const DEFAULT_DIAL_CODE = "1"; // US

const PhoneNumberField = ({
  label = "Phone Number",
  phoneNumber = "",
  onChange,
  error = false,
  helperText = "",
  disabled = false,
  required = false,
  className = "",
}) => {
  const { countries, loading } = useCountryCodes();

  const [internalError, setInternalError] = useState("");
  const [focused, setFocused] = useState(false);

  const parsedPhone = useMemo(() => {
    if (!phoneNumber) {
      return {
        countryCode: "",
        localNumber: "",
      };
    }

    if (!phoneNumber.includes("-")) {
      return {
        countryCode: DEFAULT_DIAL_CODE,
        localNumber: phoneNumber,
      };
    }

    const [countryCode, ...numberParts] = phoneNumber.split("-");

    return {
      countryCode,
      localNumber: numberParts.join("-"),
    };
  }, [phoneNumber]);

  /*
   * Find country from our API using the stored dial code.
   */
  const selectedCountry = useMemo(() => {
    if (!parsedPhone.countryCode || !countries.length) {
      return null;
    }

    const storedDialCode = parsedPhone.countryCode.replace(/\D/g, "");

    return countries.find(
      (country) =>
        String(country.dialCode).replace(/\D/g, "") === storedDialCode
    );
  }, [countries, parsedPhone.countryCode]);

  /*
   * Country used by react-phone-input-2.
   */
  const defaultCountry =
    selectedCountry?.isO2?.toLowerCase() || "us";

  /*
   * Only remount PhoneInput (via key) when the user is NOT
   * actively focused/typing. This lets us correct the displayed
   * country when new data loads (async fetch, switching records),
   * WITHOUT remounting — and losing focus — on every keystroke.
   */
  const [mountKey, setMountKey] = useState(defaultCountry);

  useEffect(() => {
    if (!focused) {
      setMountKey(defaultCountry);
    }
  }, [defaultCountry, focused]);

  /*
   * react-phone-input-2 expects:
   *
   * 19876543210
   *
   * We store:
   *
   * 1-9876543210
   *
   * Even with an empty local number, keep the dial code alone
   * (e.g. "1") so the field still shows "+1" instead of blanking
   * out entirely.
   */
  const phoneValue = useMemo(() => {
    if (!parsedPhone.countryCode && !parsedPhone.localNumber) {
      return "";
    }

    return `${parsedPhone.countryCode || ""}${parsedPhone.localNumber || ""}`;
  }, [parsedPhone]);

  /*
   * Handle phone number / country changes.
   */
  const handleChange = (value, countryData) => {
    const selected = countries.find(
      (country) =>
        country.isO2?.toLowerCase() ===
        countryData.countryCode?.toLowerCase()
    );

    /*
     * If our API doesn't contain the selected country,
     * return the raw value rather than breaking the field.
     */
    if (!selected) {
      onChange?.({
        phoneNumber: value,
      });

      return;
    }

    const dialCode = String(selected.dialCode).replace(/\D/g, "");

    let localPhoneNumber = value;

    /*
     * Remove the country dial code from the value.
     */
    if (dialCode && value.startsWith(dialCode)) {
      localPhoneNumber = value.substring(dialCode.length);
    }

    /*
     * Store:
     *
     * 91-9876543210
     *
     * Keep the dial code even when the local number is emptied
     * out (e.g. user deletes every digit) so the selected country
     * isn't lost — store "91-" rather than collapsing to "".
     */
    const formattedPhoneNumber = dialCode
      ? `${dialCode}-${localPhoneNumber}`
      : localPhoneNumber;

    setInternalError("");

    onChange?.({
      phoneNumber: formattedPhoneNumber,
    });
  };

  /*
   * MUI-style floating label:
   * shrinks to top-left when focused OR when there's a value.
   */
  const shouldShrinkLabel = focused || !!phoneValue;

  return (
    <div
      className={`phone-number-field mui-like-field ${className} ${
        error ? "phone-error" : ""
      } ${shouldShrinkLabel ? "label-shrink" : ""} ${
        focused ? "field-focused" : ""
      } ${disabled ? "field-disabled" : ""}`}
    >
      <label
        className={`mui-like-label ${
          shouldShrinkLabel ? "shrink" : ""
        }`}
      >
        {label}
        {required && " *"}
      </label>

      <PhoneInput
        country={defaultCountry}
        key={mountKey}
        value={phoneValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        enableSearch
        disableSearchIcon
        countryCodeEditable={false}
        disabled={disabled || loading}
        disableCountryGuess
        searchPlaceholder="Search"
        searchNotFound="No country found"
        inputProps={{
          required,
          name: "phoneNumber",
          type: "tel",
        }}
        containerClass="phone-input-container"
        inputClass="phone-input"
        buttonClass="phone-input-button"
        dropdownClass="phone-input-dropdown"
      />

      {(helperText || internalError) && (
        <div
          className={`phone-helper-text ${
            error || internalError ? "phone-error" : ""
          }`}
        >
          {helperText || internalError}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberField;